import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {DateTimePickerInput} from "../../Form/DateTimePickerInput";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import * as React from "react";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useBoundStore} from "../../../Store/store";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {DangerButton} from "../../Buttons/DangerButton";

export const ChangeDatesForShiftDialog = (props) => {
    const {
        open,
        onClose,
        shift
    } = props;

    const {t} = useTranslation();

    const updateShiftTimes = useBoundStore((state) => state.updateShiftTimes);
    const withdrawShift = useBoundStore((state) => state.withdrawShift);
    const updateShift = useBoundStore((state) => state.updateShift);
    const getShiftDetail = useBoundStore((state) => state.getShiftDetail);

    const [showWithdrawConfirmation, setShowWithdrawConfirmation] = useState(false);
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [breakDuration, setBreakDuration] = useState(0);
    const [isSavingBreakDuration, setIsSavingBreakDuration] = useState(false);

    const schema = yup.object({
        start_time: yup.date().required(),
        end_time: yup
            .date()
            .required()
            .test(
                "is-after-start",
                t('dialogs.change_dates.end_after_start'),
                function (value) {
                    const { start_time } = this.parent;
                    if (!start_time || !value) return true;
                    return dayjs(value).isAfter(dayjs(start_time));
                }
            ),
    }).required()

    const {
        handleSubmit,
        formState: {errors},
        setValue,
        control,
        reset,
        watch
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            start_time: null,
            end_time: null,
        }
    });

    const watchedStartTime = watch('start_time');
    const watchedEndTime = watch('end_time');

    useEffect(() => {
        if(shift && open) {
            console.log(shift)
            reset({
                start_time: shift.start_time ? dayjs(shift.start_time).toDate() : null,
                end_time: shift.end_time ? dayjs(shift.end_time).toDate() : null,
            })
            setBreakDuration(shift.break_duration ?? 0);
        }
    }, [shift, open, reset]);

    const hasChanges = () => {
        if (!shift) return false;

        const originalStartTime = shift.start_time ? dayjs(shift.start_time) : null;
        const originalEndTime = shift.end_time ? dayjs(shift.end_time) : null;
        const currentStartTime = watchedStartTime ? dayjs(watchedStartTime) : null;
        const currentEndTime = watchedEndTime ? dayjs(watchedEndTime) : null;

        const startTimeChanged = originalStartTime && currentStartTime
            ? !originalStartTime.isSame(currentStartTime)
            : originalStartTime !== currentStartTime;

        const endTimeChanged = originalEndTime && currentEndTime
            ? !originalEndTime.isSame(currentEndTime)
            : originalEndTime !== currentEndTime;

        return startTimeChanged || endTimeChanged;
    };

    const onSubmit = (data) => {
        const transformedData = {
            start_time: dayjs(data.start_time).format('YYYY-MM-DD HH:mm:ss'),
            end_time: dayjs(data.end_time).format('YYYY-MM-DD HH:mm:ss'),
        }

        updateShiftTimes(shift?.id , transformedData)
            .then(() => {
                toast.success(t('shifts.toast_saved'));
                onClose();
            })
            .catch((error) => {
                console.error('Error updating shift times', error);
            })
    }

    const handleBreakDurationSave = async () => {
        if (!shift?.id) {
            toast.error(t('shifts.not_found'));
            return;
        }

        setIsSavingBreakDuration(true);
        try {
            const updateData = {
                start_time: dayjs(shift.start_time).format('YYYY-MM-DD HH:mm:ss'),
                end_time: dayjs(shift.end_time).format('YYYY-MM-DD HH:mm:ss'),
                branch_id: shift.branch?.id,
                break_duration: breakDuration,
                is_published: shift.is_published,
                remarks: shift.remarks,
                shift_applications_possibility: shift.shift_applications_possibility ?? 'both',
            };

            await updateShift(shift.id, updateData);
            toast.success(t('shifts.break_updated'));
            await getShiftDetail(shift.id);
        } catch (error) {
            console.error('Error updating break duration:', error);
            toast.error(t('shifts.break_update_error'));
        } finally {
            setIsSavingBreakDuration(false);
        }
    };

    const handleWithdrawFreelancer = async () => {
        if (!shift?.id) {
            toast.error(t('shifts.not_found'));
            return;
        }

        setIsWithdrawing(true);
        try {
            const success = await withdrawShift(shift.id);
            if (success) {
                setShowWithdrawConfirmation(false);
                onClose();
            }
        } finally {
            setIsWithdrawing(false);
        }
    };

    return (
        <>
            <Dialog className="relative z-50" open={open} onClose={onClose}>
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
                />

                <div className="fixed inset-0 overflow-y-scroll">
                    <div className="absolute inset-0 overflow-y-scroll">
                        <div className="pointer-events-none p-4 rounded-lg fixed inset-y-0 right-0 flex max-w-full">
                            <DialogPanel
                                transition
                                className="pointer-events-auto w-screen max-w-lg transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                            >
                                <div className="flex h-full rounded-2xl flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                                    <div className="px-4 sm:px-6">
                                        <div className="flex items-start justify-between">
                                            <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                                                {t('dialogs.change_dates.title')}
                                            </DialogTitle>
                                            <div className="ml-3 flex h-7 items-center">
                                                <button
                                                    type="button"
                                                    className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                                    onClick={() => onClose()}
                                                >
                                                    <span className="absolute -inset-2.5" />
                                                    <span className="sr-only">Close panel</span>
                                                    <FontAwesomeIcon icon={faTimes} className="h-6 w-6" aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="relative mt-6 flex-1 px-4 sm:px-6">
                                        <div className="mb-8">
                                            <h3 className="text-base font-semibold text-gray-900 mb-2">
                                                {t('dialogs.change_dates.title')}
                                            </h3>
                                            <span className="text-xs text-gray-500 pb-4 block">
                                                {t('dialogs.change_dates.description')}
                                            </span>
                                            <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                                                <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 md:space-x-4'}>
                                                    <DateTimePickerInput
                                                        label={'Start time'}
                                                        name={'start_time'}
                                                        setValue={setValue}
                                                        control={control}
                                                        translationIndex={'general.fields.start_time'}
                                                        errors={errors}
                                                    />
                                                    <DateTimePickerInput
                                                        label={'End time'}
                                                        name={'end_time'}
                                                        setValue={setValue}
                                                        control={control}
                                                        translationIndex={'general.fields.end_time'}
                                                        errors={errors}
                                                    />
                                                </div>

                                                <div className={'grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'}>
                                                    <DangerButton onClick={() => {
                                                        onClose();
                                                    }} className={'order-2 md:order-1'}>
                                                        {t('general.actions.cancel')}
                                                    </DangerButton>
                                                    <PrimaryButton
                                                        onClick={handleSubmit(onSubmit)}
                                                        className={'order-1 md:order-2'}
                                                        disabled={!hasChanges()}
                                                    >
                                                        {t('dialogs.change_dates.save_adjustment')}
                                                    </PrimaryButton>
                                                </div>
                                            </form>
                                        </div>

                                        <div className="mb-8 pt-8 border-t border-gray-200">
                                            <h3 className="text-base font-semibold text-gray-900 mb-2">
                                                {t('dialogs.change_dates.break_title')}
                                            </h3>
                                            <p className="text-xs text-gray-500 mb-4">
                                                {t('dialogs.change_dates.break_description')}
                                            </p>
                                            <div className="mb-4">
                                                <label htmlFor="break_duration" className="block text-left text-sm font-medium leading-6 text-gray-900 mb-2">
                                                    {t('dialogs.change_dates.break_label')}
                                                </label>
                                                <div className="relative rounded-md shadow-sm">
                                                    <input
                                                        type="number"
                                                        id="break_duration"
                                                        name="break_duration"
                                                        value={breakDuration}
                                                        onChange={(e) => setBreakDuration(parseInt(e.target.value) || 0)}
                                                        min="0"
                                                        className="block w-full rounded-md border-0 p-1.5 pr-12 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:outline-none focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                                    />
                                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                        <span className="text-gray-500 sm:text-sm">min</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <PrimaryButton
                                                onClick={handleBreakDurationSave}
                                                disabled={isSavingBreakDuration || (shift?.break_duration ?? 0) === breakDuration}
                                                className="w-full"
                                            >
                                                {isSavingBreakDuration ? t('shifts.saving') : t('shifts.save_break')}
                                            </PrimaryButton>
                                        </div>

                                        {shift?.freelancer?.id && (
                                            <>
                                                <div className="mt-8 pt-8 border-t border-gray-200">
                                                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                                                        {t('dialogs.change_dates.withdraw_title')}
                                                    </h3>
                                                    <p className="text-xs text-gray-500 mb-4">
                                                        {t('dialogs.change_dates.withdraw_description')}
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowWithdrawConfirmation(true)}
                                                        className="w-full rounded-md border border-solid border-red-600 cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-red-600 text-white hover:bg-red-700 hover:border-red-700"
                                                    >
                                                        {t('dialogs.change_dates.withdraw_title')}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>

            {showWithdrawConfirmation && (
                <Dialog className="relative z-[60]" open={showWithdrawConfirmation} onClose={() => !isWithdrawing && setShowWithdrawConfirmation(false)}>
                    <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-50" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <DialogPanel className="bg-white rounded-lg p-6 max-w-md w-full">
                            <DialogTitle className="text-lg font-semibold text-gray-900 mb-4">
                                {t('dialogs.change_dates.withdraw_title')}
                            </DialogTitle>
                            <p className="text-gray-600 mb-6">
                                {t('dialogs.change_dates.withdraw_confirmation')}
                            </p>
                            <div className="flex gap-4 justify-end">
                                <button
                                    onClick={() => setShowWithdrawConfirmation(false)}
                                    disabled={isWithdrawing}
                                    className="rounded-md border border-solid border-gray-300 cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {t('general.actions.cancel')}
                                </button>
                                <button
                                    onClick={handleWithdrawFreelancer}
                                    disabled={isWithdrawing}
                                    className="rounded-md border border-solid border-red-600 cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-red-600 text-white hover:bg-red-700 hover:border-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isWithdrawing ? t('shifts.withdrawing') : t('shifts.confirm_withdraw')}
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </Dialog>
            )}
        </>

    )
}